/**
 * The only place that touches the File System Access API directly. Everything above it works with
 * plain values, so the grant state machine stays readable and the browser API stays swappable.
 */

// The picker, the permission query and the permission request must all name the same mode: a folder
// released for one mode does not satisfy a check made for another.
const READWRITE = { mode: "readwrite" } as const;

export function isFileSystemAccessSupported(): boolean {
  return "showDirectoryPicker" in window;
}

/**
 * Opens the native folder picker. Requires transient user activation, so it must be called
 * synchronously from a click handler — never after an awaited request.
 * Returns null when the user closes the picker without choosing.
 */
export async function pickFolder(): Promise<FileSystemDirectoryHandle | null> {
  try {
    return await window.showDirectoryPicker(READWRITE);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return null;
    }
    throw error;
  }
}

export function queryFolderAccess(handle: FileSystemDirectoryHandle): Promise<PermissionState> {
  return handle.queryPermission(READWRITE);
}

/**
 * Re-asks for a folder that was released before. Like {@link pickFolder} this needs transient user
 * activation, but it shows the permission bar rather than the picker: the user keeps their folder.
 */
export function requestFolderAccess(handle: FileSystemDirectoryHandle): Promise<PermissionState> {
  return handle.requestPermission(READWRITE);
}

/**
 * Everything a caller may hand {@link writeFileToFolder}. Deliberately excludes `string`, which
 * `BlobPart` would allow: a string is written as UTF-8, and the Word templates read Windows-1252,
 * so the encoding has to be the caller's own decision and its mistakes have to be compile errors.
 */
export type FileContent = BufferSource | Blob;

/**
 * Replaces the content of one file in the released folder.
 *
 * The browser writes into a swap file and only swaps it in on close(), so a failure leaves the
 * previous content of an existing target untouched. A target that did not exist yet is a different
 * matter: getFileHandle creates it before the write, so a failure leaves it behind, empty.
 */
export async function writeFileToFolder(
  handle: FileSystemDirectoryHandle,
  fileName: string,
  content: FileContent
): Promise<void> {
  const fileHandle = await handle.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();

  try {
    await writable.write(content);
    // Inside the try: close() is the swap-in, and it is the step that fails when the target is
    // locked or the disk is full — leaving the swap file behind is exactly what abort() prevents.
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    throw error;
  }
}
